import {properties} from "../properties";
import {ftAccessToken, ftRefreshToken, ftUserPhoto, ftUnreadCount} from "../constants/Constants";
import {useNavigate} from "react-router-dom";
import {xhrError} from "./HttpErrors";

// How many times refreshToken() waits out a 429 before giving up
const REFRESH_RATE_LIMIT_RETRIES = 3;

export function preRegister (email) {
    return new Promise((resolve, reject) => {
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else {
                    reject(xhrError(this, "Failed to register"));
                }
            }
        };
        xhttp.open("GET", properties.host + "/auth/preregister?email=" + encodeURIComponent(email), true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.send();
    });
}

export function register(firstName, lastName, email, password, confirmationToken, invitationToken, attribution) {
    return new Promise((resolve, reject) => {
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else {
                    reject(xhrError(this, "Failed to register"));
                }
            }
        };
        xhttp.open("POST", properties.host + "/auth/register", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        const user = {
            firstName: firstName,
            lastName: lastName,
            email: email,
            password: password,
            confirmationToken: confirmationToken,
            invitationToken: invitationToken || null,
            attribution: attribution || null
        };
        xhttp.send(JSON.stringify(user));
    });
}

export function authenticate(email, password) {
    return new Promise((resolve, reject) => {
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else {
                    if (this.status === 500) {
                        resolve(500);
                    } else {
                        reject(xhrError(this, "Failed to log in"));
                    }
                }
            }
        };
        xhttp.open("POST", properties.host + "/auth/authenticate", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        const user = {
            email: email,
            password: password
        };
        xhttp.send(JSON.stringify(user));
    });
}

export function logoutCall(firstCall) {
    return new Promise((resolve, reject) => {
        const accessToken = localStorage.getItem(ftAccessToken)?.replaceAll('"', '');
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    localStorage.removeItem(ftAccessToken);
                    localStorage.removeItem(ftRefreshToken);
                    localStorage.removeItem(ftUserPhoto);
                    localStorage.removeItem(ftUnreadCount);
                    resolve(true);
                    window.location.href = "/";
                    // resolve(JSON.parse(this.responseText));
                } else if (this.status === 403 && firstCall) {
                    // If the token is expired, try to refresh it
                    refreshToken().then(newAccessToken => {
                        localStorage.setItem(ftAccessToken, JSON.stringify(newAccessToken));
                        // Retry the original request with the new access token
                        logoutCall(false).then(resolve).catch(reject);
                    }).catch(() => {
                        localStorage.removeItem(ftAccessToken);
                        localStorage.removeItem(ftRefreshToken);
                        reject({ error: new Error("Failed to refresh token."), status: 403 });
                        window.location.href = "/";
                    });
                } else {
                    reject(new Error("Failed to log out."));
                }
            }
        };
        xhttp.open("GET", properties.host + "/auth/logout", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.setRequestHeader("Authorization", "Bearer " + accessToken);

        xhttp.send();
    });
}

// export function logout() {
//
//     try {
//         logoutCall(true).then(r => {
//             localStorage.removeItem(ftAccessToken);
//             localStorage.removeItem(ftRefreshToken);
//         } );
//
//         return true;
//     } catch (e) {
//         return false;
//     }
//
// }

export function isUserLoggedInCall() {
    const token = localStorage.getItem(ftAccessToken);
    if (!token || token === 'undefined') {
        return false;
    }

    try {
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const json = decodeURIComponent(atob(base64).split('').map(c =>
            '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
        ).join(''));
        const { exp, nbf } = JSON.parse(json);

        const nowSec = Math.floor(Date.now() / 1000);
        const skew = 60; // optional clock skew

        // const valid = Number(exp) > nowSec + skew && (!nbf || Number(nbf) <= nowSec + skew);
        const valid = exp > nowSec;
        return valid
    } catch (e) {
        console.error('Error checking JWT expiration:', e);
        return false;
    }
}

export function isUserLoggedIn() {
    const token = localStorage.getItem(ftAccessToken);
    if (!token || token === 'undefined') {
        return false;
    }
    // First pass with the current access token
    const ok = isUserLoggedInCall();
    if (ok) return true;

    // If first try failed (likely expired), refresh once and try again (firstCall = false)
    const newAccessToken = refreshTokenSync(); // sync call
    if (newAccessToken) {
        localStorage.setItem(ftAccessToken, newAccessToken);
        return isUserLoggedInCall();
    }

    return false;
}

export function refreshTokenSync() {
    // Synchronous XHR; returns access token string or null
    const rt = localStorage.getItem(ftRefreshToken)?.replaceAll('"', '');
    if (!rt) return null;

    const xhttp = new XMLHttpRequest();
    xhttp.open("POST", properties.host + "/auth/refresh", false); // sync request
    xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
    xhttp.setRequestHeader("Authorization", "Bearer " + rt);

    try {
        xhttp.send();
        if (xhttp.status === 200) {
            const response = JSON.parse(xhttp.responseText);
            return response?.access_token ?? null;
        } else if (xhttp.status === 403) {
            localStorage.removeItem(ftAccessToken);
            localStorage.removeItem(ftRefreshToken);
        }
    } catch (error) {
        console.error(error);
        localStorage.removeItem(ftAccessToken);
        localStorage.removeItem(ftRefreshToken);
    }
    return null;
}


export function getJwtRole() {
    const token = localStorage.getItem(ftAccessToken);
    if (!token) {
        return null;
    }
    try {
        const base64Url = token.split('.')[1]; // Get the payload part of the token
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));

        const { role } = JSON.parse(jsonPayload); // Extract the role from the payload
        return role;
    } catch (error) {
        console.error('Error extracting role from JWT:', error);
        return null;
    }
}

export function getJwtIsReviewer() {
    const token = localStorage.getItem(ftAccessToken);
    if (!token) return false;
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const { isReviewer } = JSON.parse(jsonPayload);
        return isReviewer === true;
    } catch {
        return false;
    }
}

// A 429 from /auth/refresh means the app-wide rate limit, not a dead session:
// wait out Retry-After and retry, and never redirect over it. If it is still
// limited after the retries, reject with the 429 so callers can tell.
export function refreshToken(attempt = 0) {
    return new Promise((resolve, reject) => {
        // Retrieve the refresh token from storage
        const refreshToken = localStorage.getItem(ftRefreshToken)?.replaceAll('"', '');
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    // Parse the response and resolve the promise with the new access token
                    const response = JSON.parse(this.responseText);
                    resolve(response.access_token);
                } else if (this.status === 429 && attempt < REFRESH_RATE_LIMIT_RETRIES) {
                    const waitSeconds = Math.min(Number(this.getResponseHeader("Retry-After")) || 1, 10);
                    setTimeout(() => refreshToken(attempt + 1).then(resolve, reject), waitSeconds * 1000);
                } else if (this.status === 429) {
                    reject(xhrError(this, "Failed to refresh token."));
                } else {
                    // If the refresh token request fails, reject the promise
                    window.location.href = "/";
                    reject({ error: new Error("Failed to refresh token."), status: 403 });
                }
            }
        };
        xhttp.open("POST", properties.host + "/auth/refresh", false);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        // Set the Authorization header with the refresh token
        xhttp.setRequestHeader("Authorization", "Bearer " + refreshToken);

        xhttp.send();
    });
}

export function authenticateGoogle(request) {
    return new Promise((resolve, reject) => {
        let xhttp = new XMLHttpRequest();
        xhttp.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    resolve(JSON.parse(this.responseText));
                } else {
                    if (this.status === 500) {
                        resolve(500);
                    } else {
                        reject(xhrError(this, "Failed to log in"));
                    }
                }
            }
        };
        xhttp.open("POST", properties.host + "/auth/google/authenticate", true);
        xhttp.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhttp.send(JSON.stringify(request));
    });
}